import { z } from 'zod';
import { uuid } from './contratos.js';

/**
 * Etiquetas RFID — lector HID 13.56 MHz (Mifare, EM4100…).
 *
 * El lector emula un teclado: al acercar la tarjeta «tipea» el UID en hexadecimal
 * seguido de Enter. Por eso el UID llega como texto libre y se normaliza aquí a
 * mayúsculas sin separadores; la base lo guarda con `citext` igual.
 */
const uidRfid = z
  .string()
  .trim()
  .transform((valor) => valor.toUpperCase())
  .pipe(
    z
      .string()
      .min(4, 'El UID es demasiado corto')
      .max(32, 'El UID es demasiado largo')
      .regex(/^[0-9A-F]+$/, 'El UID solo puede tener caracteres hexadecimales'),
  );

export const vincularRfidSchema = z.object({
  activoId: uuid,
  uid: uidRfid,
});
export type VincularRfidEntrada = z.infer<typeof vincularRfidSchema>;

export const escaneoRfidSchema = z.object({
  uid: uidRfid,
  /** Punto de lectura fijo, si el lector está asociado a una ubicación. */
  ubicacionId: uuid.optional(),
});
export type EscaneoRfidEntrada = z.infer<typeof escaneoRfidSchema>;

/**
 * Paso 1 del lector: la web reconoce el dispositivo por la forma de sus teclas
 * (ráfaga rápida que termina en Enter) y lo reporta con su duración.
 */
export const reconocimientoLectorSchema = z.object({
  uid: uidRfid,
  duracionMs: z.number().int().min(0).max(60000),
  caracteres: z.number().int().min(1).max(64),
});
export type ReconocimientoLectorEntrada = z.infer<typeof reconocimientoLectorSchema>;

/** Resultado de un escaneo: qué activo es y si está donde debería. */
export interface ResultadoEscaneoRfid {
  activo: { id: string; nombre: string };
  ubicacionEsperada: string | null;
  /** Donde se escaneó, si el lector tiene ubicación fija. */
  ubicacionLector: string | null;
  /** `null` cuando no se puede decir (no hay lector con ubicación o el activo no tiene ubicación). */
  fueraDeLugar: boolean | null;
  fecha: string;
}
